import { useCallback } from 'react';
import {
  useList,
  useCreate,
  useNotification,
  useCustomMutation,
  useApiUrl,
} from '@refinedev/core';
import { HttpError } from '@refinedev/core';
import {
  ExportJob,
  ExportJobDownloadUrl,
  ExportKind,
} from '@aphp-ema/common/export';
import { useRoles } from '@/core/auth/useRoles';

export const useExports = (studyId: string) => {
  const { open: openNotification } = useNotification();
  const { data: rolesData } = useRoles();

  const apiUrl = useApiUrl();
  const { mutateAsync } = useCustomMutation<ExportJobDownloadUrl>();

  // Determine export kind based on user role
  const isAdmin = rolesData?.some((role) => role.name === 'admin');
  const exportKind: ExportKind = isAdmin ? 'ADMIN' : 'RESEARCHERS';

  const {
    data: exportJobsData,
    isLoading,
    refetch,
  } = useList<ExportJob>({
    resource: `admin/studies/${studyId}/exports`,
    pagination: {
      mode: 'off',
    },
    queryOptions: {
      staleTime: 5000,
      refetchInterval: 5000,
    },
  });

  const exportJobs = exportJobsData?.data || [];

  const { mutateAsync: createExportJob, isLoading: isCreatingExport } =
    useCreate<ExportJob, HttpError>({
      resource: `admin/studies/${studyId}/exports`,
      mutationOptions: {
        onSuccess: () => {
          openNotification?.({
            type: 'success',
            message: 'Export started successfully',
          });
          refetch();
        },
        onError: (error: HttpError) => {
          openNotification?.({
            type: 'error',
            message: 'Failed to start export',
            description: error.message,
          });
        },
      },
    });

  const startExport = useCallback(async () => {
    await createExportJob({
      values: {
        kind: exportKind,
      },
    });
  }, [createExportJob, exportKind]);

  const downloadExport = useCallback(
    async (id: string) => {
      const { data } = await mutateAsync({
        url: `${apiUrl}/admin/studies/${studyId}/exports/${id}/download`,
        method: 'post',
        values: {},
      });
      window.open(data.downloadUrl, '_blank');
    },
    [mutateAsync, apiUrl, studyId],
  );

  return {
    exportJobs,
    isLoading,
    isCreatingExport,
    startExport,
    downloadExport,
    refetch,
  };
};
